import { contactSchema, type ContactCall, type ContactDetail, type ContactInput, type ContactSummary, type Me } from '@smarton-pbx/shared';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { ArrowLeft, BookUser, Loader2, MessageSquare, Pencil, Phone, PhoneIncoming, PhoneOutgoing, Plus, Search, Trash2, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../api';
import { Dialog, EmptyState, Field, Loading, Notice, opensOnClick, PageHeader } from '../components/ui';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { locale, t } from '../i18n';
import { formatNumber, outcome } from '../lib/calls';
import { useLoad, useSubmit } from '../lib/hooks';
import { duration } from './Calls';
import { usePhone } from './softphone';

const when = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });

function contactColumns(): Column<ContactSummary>[] {
  return [
    {
      key: 'name',
      label: t('Name'),
      fixed: true,
      cell: (c) => (
        <>
          <Link to={`/contacts/${c.id}`} className="name">
            {c.name}
          </Link>
          {c.company && <div className="detail subtle">{c.company}</div>}
        </>
      ),
      excel: [
        { label: t('Name'), value: (c) => c.name },
        { label: t('Company'), value: (c) => c.company },
      ],
    },
    {
      key: 'numbers',
      label: t('Numbers'),
      className: 'tabular',
      cell: (c) => c.numbers.map((n) => formatNumber(n.e164)).join(', ') || '—',
      value: (c) => c.numbers.map((n) => n.e164).join(', '),
    },
    { key: 'email', label: t('Email'), hidden: true, className: 'hide-mobile muted', cell: (c) => c.email ?? '—', value: (c) => c.email },
    {
      key: 'lastCall',
      label: t('Last call'),
      className: 'hide-mobile muted',
      cell: (c) => (c.lastCallAt ? when(c.lastCallAt) : '—'),
      value: (c) => (c.lastCallAt ? new Date(c.lastCallAt) : null),
    },
  ];
}

/** Everyone's contacts: search by name, company, email or number. */
export function ContactsPage({ me }: { me: Me }) {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const contacts = useLoad(() => api.listContacts(search), [search]);
  // The page shows the first 200; the download has them all.
  const table = useTable('contacts', contactColumns(), mayDownload(me.role) ? { name: t('Contacts'), rows: () => api.listContacts(search, 10_000) } : null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="stack">
      <PageHeader title={t('Contacts')} description={t('Customers and others your team talks to: their numbers, notes, and every call with them and what it was about.')}>
        <button type="button" onClick={() => setCreating(true)} data-testid="add-contact">
          <Plus size={16} aria-hidden /> {t('New contact')}
        </button>
      </PageHeader>
      <div className="search-box">
        <Search size={16} aria-hidden className="subtle" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Name, company, email or number')} aria-label={t('Search contacts')} data-testid="contact-search" />
      </div>
      {contacts.error && <Notice kind="error">{contacts.error}</Notice>}
      {!contacts.data ? (
        <Loading />
      ) : contacts.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={BookUser} title={search ? t('No contact matches') : t('No contacts yet')}>
            <p>{t('Add one here, or save a caller from a call.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {contacts.data.map((c) => (
                <tr key={c.id} data-testid="contact-row" {...opensOnClick(() => navigate(`/contacts/${c.id}`))}>
                  <Cells table={table} row={c} />
                  <td className="actions" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={creating} onClose={() => setCreating(false)} title={t('New contact')} wide>
        {creating && (
          <ContactForm
            me={me}
            contact={null}
            onCancel={() => setCreating(false)}
            onSaved={(saved) => {
              setCreating(false);
              navigate(`/contacts/${saved.id}`);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** A contact's card: who they are, notes, and every call with them with what it was about. */
export function ContactPage({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const contact = useLoad(() => api.getContact(id), [id]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string>();
  const navigate = useNavigate();
  const phone = usePhone();
  const canDelete = me.role !== 'AGENT';

  // Summaries still being written: look again in a while.
  const pending = contact.data?.calls.some((c) => c.summaryPending);
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(contact.reload, 5000);
    return () => clearTimeout(timer);
  }, [pending, contact.data, contact.reload]);

  async function remove() {
    if (!contact.data || !confirm(t('Delete {name}? Their calls stay.', { name: contact.data.name }))) return;
    try {
      await api.deleteContact(contact.data.id);
      navigate('/contacts');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (contact.error) return <Notice kind="error">{contact.error}</Notice>;
  if (!contact.data) return <Loading />;
  const c = contact.data;
  return (
    <div className="stack">
      <Link to="/contacts" className="back-link">
        <ArrowLeft size={16} aria-hidden /> {t('Contacts')}
      </Link>
      <div className="card card-body contact-head">
        <span className="avatar square large" aria-hidden>
          {c.name
            .split(/\s+/)
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase()}
        </span>
        <div className="grow">
          <h1 className="call-title" data-testid="contact-name">
            {c.name}
          </h1>
          <div className="call-meta">
            {c.company && <span>{c.company}</span>}
            {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
            {c.updatedBy && <span>{t('Card by {name}', { name: c.updatedBy })}</span>}
          </div>
        </div>
        <div className="row">
          <button type="button" className="secondary" onClick={() => setEditing(true)} data-testid="edit-contact">
            <Pencil size={16} aria-hidden /> {t('Edit')}
          </button>
          {canDelete && (
            <button type="button" className="ghost icon" aria-label={t('Delete contact')} onClick={() => void remove()}>
              <Trash2 size={18} aria-hidden />
            </button>
          )}
        </div>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      <div className="contact-grid">
        <div className="stack">
          <div className="card">
            <div className="card-header">
              <h2>{t('Numbers')}</h2>
            </div>
            <div className="card-body stack" style={{ gap: '0.5rem' }}>
              {c.numbers.length === 0 && <p className="subtle">{t('No numbers yet.')}</p>}
              {c.numbers.map((n) => (
                <div key={n.e164} className="row" style={{ justifyContent: 'space-between' }}>
                  <span>
                    <span className="tabular">{formatNumber(n.e164)}</span>
                    {n.label && <span className="subtle"> · {n.label}</span>}
                  </span>
                  <span className="row" style={{ gap: '0.4rem' }}>
                    <Link
                      to={`/texts/new?to=${encodeURIComponent(n.e164)}`}
                      className="button secondary small"
                      aria-label={t('Text {name}', { name: formatNumber(n.e164) })}
                      data-testid="contact-text"
                    >
                      <MessageSquare size={14} aria-hidden /> {t('Text')}
                    </Link>
                    <button
                      type="button"
                      className="secondary small"
                      disabled={phone.status !== 'registered' || phone.busy}
                      onClick={() => phone.dial(n.e164)}
                      aria-label={t('Call {name}', { name: formatNumber(n.e164) })}
                    >
                      <Phone size={14} aria-hidden /> {t('Call')}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <h2>{t('Notes')}</h2>
            </div>
            <div className="card-body">{c.notes ? <p className="contact-notes">{c.notes}</p> : <p className="subtle">{t('None yet. Edit the card to add what the team should know.')}</p>}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <h2>{t('Calls')}</h2>
          </div>
          <div className="card-body">
            {c.calls.length === 0 ? <p className="subtle">{t('No calls with these numbers yet.')}</p> : <CallTimeline calls={c.calls} />}
          </div>
        </div>
      </div>
      <Dialog open={editing} onClose={() => setEditing(false)} title={t('Edit {name}', { name: c.name })} wide>
        {editing && (
          <ContactForm
            me={me}
            contact={c}
            onCancel={() => setEditing(false)}
            onSaved={(saved) => {
              setEditing(false);
              contact.setData(saved);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** Calls with someone, newest first, each with what it was about. */
export function CallTimeline({
  calls,
  compact,
  who,
}: {
  calls: ContactCall[];
  compact?: boolean;
  /** Whose number each is (number → name), where there are several people to tell apart. */
  who?: Record<string, string>;
}) {
  return (
    <ol className={`call-timeline ${compact ? 'compact' : ''}`} data-testid="call-timeline">
      {calls.map((call) => {
        const result = outcome({ outcome: call.outcome, answeredBy: null });
        const Icon = call.direction === 'INBOUND' ? PhoneIncoming : PhoneOutgoing;
        return (
          <li key={call.callId}>
            <div className="timeline-head">
              <Icon size={14} aria-hidden className="subtle" />
              {/* A colleague's call, for an agent: what was said, but not the call itself. */}
              {call.openable ? (
                <Link to={`/calls/${call.callId}`} className="plain-link">
                  {when(call.startedAt)}
                </Link>
              ) : (
                <span>{when(call.startedAt)}</span>
              )}
              {who?.[call.number] && <span className="timeline-who">{who[call.number]}</span>}
              <span className="subtle">
                {call.userName ? `· ${call.userName}` : ''}
                {call.durationSeconds ? ` · ${duration(call.durationSeconds)}` : ''}
                {call.outcome && call.outcome !== 'ANSWERED' ? ` · ${result.label}` : ''}
              </span>
            </div>
            {call.summary ? (
              <p className="timeline-summary" data-testid="call-summary">
                {call.summary}
              </p>
            ) : call.summaryPending ? (
              <p className="timeline-summary subtle">
                <Loader2 size={12} className="spin" aria-hidden /> {t('Summary on its way')}
              </p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

type NumberRow = { e164: string; label: string };

/** A contact's details. Numbers as people write them; saved with the country code. */
export function ContactForm({
  me,
  contact,
  initialNumber,
  onCancel,
  onSaved,
}: {
  me: Me;
  contact: ContactDetail | null;
  /** A caller's number, when saving them from a call. */
  initialNumber?: string;
  onCancel: () => void;
  onSaved: (saved: ContactDetail) => void;
}) {
  const [name, setName] = useState(contact?.name ?? '');
  const [company, setCompany] = useState(contact?.company ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const [numbers, setNumbers] = useState<NumberRow[]>(
    contact?.numbers.length
      ? contact.numbers.map((n) => ({ e164: formatNumber(n.e164), label: n.label ?? '' }))
      : [{ e164: initialNumber ? formatNumber(initialNumber) : '', label: '' }],
  );
  const form = useSubmit();
  const country = (me.region?.countryCode ?? 'LT') as CountryCode;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const input: ContactInput = {
      name,
      company: company || null,
      email: email || null,
      notes,
      numbers: numbers
        .filter((n) => n.e164.trim())
        .map((n) => ({ e164: parsePhoneNumberFromString(n.e164, country)?.number ?? n.e164.replace(/[\s()-]/g, ''), label: n.label || null })),
    };
    const saved = await form.submit(contactSchema, input, (value) => (contact ? api.updateContact(contact.id, value) : api.createContact(value)));
    if (saved) onSaved(saved);
  }

  const numberError = Object.entries(form.fields).find(([k]) => k.startsWith('numbers'))?.[1];
  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <div className="form-grid">
          <Field label={t('Name')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Jonas Jonaitis" data-testid="contact-form-name" />
          </Field>
          <Field label={t('Company')} hint={t('Optional')}>
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="UAB Pavyzdys" data-testid="contact-form-company" />
          </Field>
          <Field label={t('Email')} hint={t('Optional')} error={form.fields.email}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>
        <Field label={t('Numbers')} group error={numberError}>
          <div className="stack" style={{ gap: '0.4rem' }}>
            {numbers.map((n, i) => (
              <div key={i} className="row">
                <input
                  value={n.e164}
                  onChange={(e) => setNumbers(numbers.map((x, j) => (j === i ? { ...x, e164: e.target.value } : x)))}
                  placeholder="+370 600 00000"
                  inputMode="tel"
                  aria-label={t('Number')}
                  className="tabular"
                  data-testid="contact-form-number"
                />
                <input
                  value={n.label}
                  onChange={(e) => setNumbers(numbers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  placeholder={t('Mobile, office…')}
                  aria-label={t('What number it is')}
                  style={{ maxWidth: '11rem' }}
                />
                <button type="button" className="ghost icon" aria-label={t('Remove number')} onClick={() => setNumbers(numbers.filter((_, j) => j !== i))}>
                  <X size={16} aria-hidden />
                </button>
              </div>
            ))}
            <button type="button" className="ghost small" style={{ justifySelf: 'start' }} onClick={() => setNumbers([...numbers, { e164: '', label: '' }])}>
              <Plus size={14} aria-hidden /> {t('Number')}
            </button>
          </div>
        </Field>
        <Field label={t('Notes')} hint={t('What the team should know: what they bought, how they like to be contacted…')}>
          <textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="contact-form-notes" />
        </Field>
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="contact-form-save">
          {t('Save')}
        </button>
      </div>
    </form>
  );
}
